import {
  CircularProgress,
  TableCell as MuiTableCell,
  TableHead as MuiTableHead,
  TableHeadProps as MuiTableHeadProps,
  Table,
  TableBody,
  TableRow,
  TableSortLabel,
  Typography,
  makeStyles,
} from "@material-ui/core"
import clsx from "clsx"
import React, { FC, MouseEvent, useState } from "react"
import { useHistory } from "react-router-dom"
import useCasemix from "../../hooks/useCasemix"
import { Pending } from "../../utils/constants"
import { getComparator, stableSort } from "../../utils/helpers"
import { colors } from "../../utils/theme"
import { Order, StaffTableData } from "../../utils/types"
import ChevronDownIcon from "../atoms/ChevronDownIcon"
import TableCell from "../atoms/TableCell"
import StaffMoreButton from "./StaffMoreButton"

const CASEMIX_WIDTH = 388

const useStyles = makeStyles((theme) => ({
  table: {
    color: colors.neutral.denim,
    borderRadius: 0,
    boxShadow: "none",
    border: `1px solid ${colors.neutral.clay}`,
    backgroundColor: colors.neutral.white,
    width: "100%",
  },
  tableHeaderCell: {
    color: colors.neutral.denim,
    fontWeight: 700,
    borderBottom: `1px solid ${colors.neutral.clay}`,
    verticalAlign: "top",
  },
  tableSortLabel: {
    color: `${colors.neutral.denim} !important`,
    fontWeight: 700,
  },
  sublabel: {
    color: colors.neutral.denim_08,
    textAlign: "right",
    fontWeight: 400,
  },
  noData: {
    width: "100%",
    padding: 27,
    textAlign: "center",
    border: `1px solid ${colors.neutral.clay}`,
    borderTop: 0,
    backgroundColor: colors.neutral.white,
  },
  noDataFirstLine: {
    fontWeight: 700,
    marginBottom: 6,
  },
  pending: {
    color: colors.neutral.asphalt,
    fontStyle: "italic",
  },
  questionnaireColumn: {
    display: "flex",
    alignItems: "center",
  },
  questionnaireScore: {
    height: 33,
    width: 33,
    borderRadius: 4,
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  casemix: {
    height: 8,
    width: CASEMIX_WIDTH,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
  },
  casemixSection: {
    height: 8,
  },
  italic: {
    fontStyle: "italic",
  },
  center: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}))

interface Column {
  id: keyof StaffTableData
  label: string
  sublabel?: string
  align?: "right" | "left"
}

type TableHeadProps = {
  order: Order
  orderBy: string
  onRequestSort: (
    event: MouseEvent<unknown>,
    property: keyof StaffTableData
  ) => void
} & MuiTableHeadProps

const TableHead: FC<TableHeadProps> = ({ order, orderBy, onRequestSort }) => {
  const classes = useStyles()
  const createSortHandler =
    (property: keyof StaffTableData) => (event: MouseEvent<unknown>) => {
      onRequestSort(event, property)
    }
  const columns: Column[] = [
    {
      id: "lastName",
      label: "Last Name",
    },
    {
      id: "firstName",
      label: "First Name",
    },
    {
      id: "viraClients",
      label: "Vira Clients",
      align: "right",
    },
    {
      id: "nudges",
      label: "Nudges",
      sublabel: "total",
      align: "right",
    },
    {
      id: "questionnaires",
      label: "Assessments",
      sublabel: "total",
      align: "right",
    },
    {
      id: "casemix",
      label: "Casemix",
    },
    {
      id: "more",
      label: "",
    },
  ]
  return (
    <MuiTableHead>
      <TableRow>
        {columns.map((column) => (
          <MuiTableCell
            key={column.id}
            sortDirection={orderBy === column.id ? order : false}
            className={classes.tableHeaderCell}
            align={
              column.label === "" || column.align === "right"
                ? "right"
                : undefined
            }
            padding={column.label === "" ? "none" : undefined}
          >
            <TableSortLabel
              active={orderBy === column.id}
              direction={orderBy === column.id ? order : "asc"}
              onClick={createSortHandler(column.id)}
              IconComponent={(props) => <ChevronDownIcon {...props} />}
              classes={{
                root: classes.tableSortLabel,
                active: classes.tableSortLabel,
              }}
            >
              {column.label}
            </TableSortLabel>
            {column.sublabel && (
              <Typography variant="body2" className={classes.sublabel}>
                {column.sublabel}
              </Typography>
            )}
          </MuiTableCell>
        ))}
      </TableRow>
    </MuiTableHead>
  )
}

/**
 * Renders the casemix progress bar for a practitioner.
 *
 * @param practitionerId - The ID of the practitioner.
 */
function Casemix(props: { practitionerId: string }) {
  const { practitionerId } = props
  const classes = useStyles()
  const { getCasemixForPractitioner, categoryDisplayValues } = useCasemix()
  const casemixForPractitioner = getCasemixForPractitioner(practitionerId)
  const casemixSections = Array.from(casemixForPractitioner.keys())
    .sort()
    .map((key) => {
      const count = casemixForPractitioner.get(key) ?? 0
      const color = categoryDisplayValues.get(key)?.color ?? colors.neutral.clay
      return (
        <div
          key={key + practitionerId}
          className={classes.casemixSection}
          style={{
            width: CASEMIX_WIDTH * count,
            backgroundColor: color,
          }}
        />
      )
    })

  return (
    <div className={classes.casemix}>
      {casemixSections}
      {casemixSections.length === 0 && (
        <div
          className={classes.casemixSection}
          style={{ width: CASEMIX_WIDTH, backgroundColor: colors.neutral.clay }}
        />
      )}
    </div>
  )
}

function padLeft(score: number) {
  if (score < 10) {
    return `0${score}`
  } else {
    return score.toString()
  }
}

const StaffTable: FC<{
  staffData?: StaffTableData[]
}> = ({ staffData }) => {
  const classes = useStyles()
  const [order, setOrder] = useState<Order>("asc")
  const [orderBy, setOrderBy] = useState<keyof StaffTableData>("lastName")
  const history = useHistory()

  function onClick(row: StaffTableData) {
    if (row.viraClients.data !== "Pending") {
      history.push(`/practitioner-detail/${row.id.data}`)
    }
  }

  return (
    <>
      <Table className={classes.table}>
        <TableHead
          order={order}
          orderBy={orderBy}
          onRequestSort={(
            event: React.MouseEvent<unknown>,
            property: keyof StaffTableData
          ) => {
            const isAsc = orderBy === property && order === "asc"
            setOrder(isAsc ? "desc" : "asc")
            setOrderBy(property)
          }}
        />
        {staffData && staffData.length > 0 && (
          <TableBody>
            {stableSort(staffData, getComparator(order, orderBy)).map(
              (row, index) => (
                <TableRow key={index}>
                  <TableCell
                    role="button"
                    onClick={() => onClick(row)}
                    align={row.lastName.align}
                    className={
                      row.viraClients.data === Pending
                        ? classes.italic
                        : undefined
                    }
                  >
                    {row.lastName.data}
                  </TableCell>
                  <TableCell
                    role="button"
                    onClick={() => onClick(row)}
                    align={row.firstName.align}
                    className={
                      row.viraClients.data === Pending
                        ? classes.italic
                        : undefined
                    }
                  >
                    {row.firstName.data}
                  </TableCell>
                  <TableCell
                    role="button"
                    onClick={() => onClick(row)}
                    align="right"
                    className={
                      row.viraClients.data === Pending
                        ? classes.italic
                        : undefined
                    }
                  >
                    {row.viraClients.data === Pending
                      ? Pending
                      : padLeft(row.viraClients.data as number)}
                  </TableCell>
                  <TableCell
                    role="button"
                    onClick={() => onClick(row)}
                    align="right"
                  >
                    {row.nudges.data !== "" &&
                      padLeft(row.nudges.data as number)}
                  </TableCell>
                  <TableCell
                    role="button"
                    onClick={() => onClick(row)}
                    align="right"
                  >
                    {row.questionnaires.data !== "" &&
                      padLeft(row.questionnaires.data as number)}
                  </TableCell>
                  <TableCell
                    role="button"
                    onClick={() => onClick(row)}
                    align={row.casemix.align}
                  >
                    <Casemix practitionerId={(row.id.data as string) ?? ""} />
                  </TableCell>
                  <TableCell align="right" padding="none">
                    <StaffMoreButton
                      staffId={row.id.data as string}
                      firstName={row.firstName.data}
                      lastName={row.lastName.data}
                      email={row.more.data.email}
                      role={row.more.data.role}
                      isPending={row.viraClients.data === Pending}
                    />
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        )}
      </Table>

      {staffData && staffData.length === 0 && (
        <div className={classes.noData}>
          <Typography variant="body1" className={classes.noDataFirstLine}>
            You currently do not have any staff members listed on your
            dashboard.
          </Typography>
          <Typography variant="body1">
            To add your first staff member, tap the “+” button on the upper
            left.
          </Typography>
        </div>
      )}

      {!staffData && (
        <div className={clsx(classes.noData, classes.center)}>
          <CircularProgress />
        </div>
      )}
    </>
  )
}

export default StaffTable
